import { createContext, useCallback, useContext, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { CalendarCheck, PartyPopper, Send, Sparkles } from 'lucide-react';
import { publicApi } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';
import {
    RESERVATION_TYPES,
    RESERVATIONS_ENABLED,
    RESERVATION_DISABLED_MESSAGE,
} from '@/lib/data';


const Ctx = createContext({ openReservation: () => {} });

const INITIAL = {
    name: '',
    phone: '',
    email: '',
    reservation_type: 'visita_general',
    event_date: '',
    people_count: '',
    message: '',
    preferred_channel: 'whatsapp',
};

export const ReservationProvider = ({ children }) => {
    const [open, setOpen] = useState(false);
    const [defaults, setDefaults] = useState({});

    const openReservation = useCallback((data = {}) => {
    if (!RESERVATIONS_ENABLED) {
        toast.info(RESERVATION_DISABLED_MESSAGE);
        return;
    }

    setDefaults(data);
    setOpen(true);
    }, []);

    const value = { openReservation };

    return (
        <Ctx.Provider value={value}>
            {children}
            <ReservationModal open={open} onOpenChange={setOpen} defaults={defaults} />
        </Ctx.Provider>
    );
};

export const useReservation = () => useContext(Ctx);

export const ReservationModal = ({ open, onOpenChange, defaults = {} }) => {
    const [form, setForm] = useState({ ...INITIAL, ...defaults });
    const [submitting, setSubmitting] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !(form.phone || form.email)) {
            toast.error('Por favor incluye tu nombre y al menos un dato de contacto.');
            return;
        }
        setSubmitting(true);
        try {
            await publicApi.submitReservation({
                ...form,
                people_count: Number(form.people_count) || 0,
                source_page: window.location.pathname,
            });
            trackEvent('reservation_submit', window.location.pathname, { reservation_type: form.reservation_type });
            toast.success('¡Reserva enviada! Te contactaremos muy pronto.');
            setForm({ ...INITIAL });
            onOpenChange(false);
        } catch {
            toast.error('No pudimos enviar tu reserva. Inténtalo nuevamente.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="reservation-modal" className="max-w-2xl max-h-[92vh] overflow-y-auto p-0 rounded-3xl border-0">
                <div className="bg-gradient-fun text-white px-6 sm:px-8 pt-6 pb-8 rounded-t-3xl relative overflow-hidden">
                    <div className="absolute -top-10 -right-10 w-40 h-40 bg-[hsl(var(--kche-yellow))]/30 rounded-full blur-2xl" aria-hidden="true" />
                    <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-[hsl(var(--kche-cyan))]/30 rounded-full blur-2xl" aria-hidden="true" />
                    <DialogHeader className="relative">
                        <span className="inline-flex items-center gap-2 self-start rounded-full bg-white/20 backdrop-blur px-3 py-1 text-xs font-bold text-white"><Sparkles className="size-3.5" /> Reserva Online</span>
                        <DialogTitle className="font-display text-2xl sm:text-3xl text-white mt-3">¡Vive tu aventura en K\'che!</DialogTitle>
                        <DialogDescription className="text-white/90 font-body">Reserva tu visita, cumpleaños o evento. Te contactaremos para confirmar los detalles.</DialogDescription>
                    </DialogHeader>
                </div>
                <form onSubmit={onSubmit} className="p-6 sm:p-8 grid gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2"><Label htmlFor="r-name">Nombre completo *</Label><Input id="r-name" data-testid="reservation-name" value={form.name} onChange={(e) => set('name', e.target.value)} className="mt-1" placeholder="Tu nombre" /></div>
                    <div><Label htmlFor="r-phone">Teléfono / WhatsApp</Label><Input id="r-phone" data-testid="reservation-phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} className="mt-1" placeholder="+593 ..." /></div>
                    <div><Label htmlFor="r-email">Email</Label><Input id="r-email" data-testid="reservation-email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="mt-1" placeholder="correo@ejemplo.com" /></div>
                    <div><Label htmlFor="r-type">Tipo de reserva</Label>
                        <Select value={form.reservation_type} onValueChange={(v) => set('reservation_type', v)}>
                            <SelectTrigger id="r-type" data-testid="reservation-type" className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>{RESERVATION_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div><Label htmlFor="r-date">Fecha estimada</Label><Input id="r-date" data-testid="reservation-date" type="date" value={form.event_date} onChange={(e) => set('event_date', e.target.value)} className="mt-1" /></div>
                    <div><Label htmlFor="r-people">Número aproximado de personas</Label><Input id="r-people" data-testid="reservation-people" type="number" min={1} value={form.people_count} onChange={(e) => set('people_count', e.target.value)} className="mt-1" placeholder="Ej: 12" /></div>
                    <div className="sm:col-span-2"><Label htmlFor="r-msg">Mensaje</Label><Textarea id="r-msg" data-testid="reservation-message" rows={3} value={form.message} onChange={(e) => set('message', e.target.value)} className="mt-1" placeholder="Cuéntanos más sobre tu visita o evento…" /></div>
                    <div className="sm:col-span-2">
                        <Label>Canal preferido de contacto</Label>
                        <div className="mt-1 flex flex-wrap gap-3 text-sm">
                            {[['whatsapp', 'WhatsApp'], ['phone', 'Llamada'], ['email', 'Email']].map(([k, l]) => (
                                <label key={k} className="inline-flex items-center gap-2 cursor-pointer rounded-full border border-border px-3 py-1.5 has-[:checked]:bg-[hsl(var(--kche-pink))] has-[:checked]:text-white has-[:checked]:border-[hsl(var(--kche-pink))] transition-colors">
                                    <input type="radio" name="channel" value={k} checked={form.preferred_channel === k} onChange={(e) => set('preferred_channel', e.target.value)} className="sr-only" />
                                    {l}
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="sm:col-span-2 flex justify-end pt-2">
                        <button type="submit" disabled={submitting} data-testid="reservation-submit" className="inline-flex items-center gap-2 rounded-full bg-gradient-fun text-white font-bold px-6 py-3 text-sm shadow-pop hover:brightness-110 active:brightness-95 disabled:opacity-60 transition">
                            <Send className="size-4" /> {submitting ? 'Enviando…' : 'Confirmar reserva'}
                        </button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default ReservationProvider;
